import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Headphones, MessageCircle, MessagesSquare, ShieldCheck, Smartphone, History } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { API_BASE_URL } from '../../apiBase';
import { supportContacts, supportHref } from '../../lib/supportChannels';
import { WhatsAppIcon } from '../ServiceBrandIcon';
import './SupportChannels.css';

export default function SupportChoiceModal({ open, onOpenChange, supportLabel = 'Suporte',
  hasAtendimento = false, onAtendimento }) {
  const [contacts, setContacts] = useState([]);
  const [preferred, setPreferred] = useState(null);
  const [selected, setSelected] = useState('');
  const [loading, setLoading] = useState(false);
  const [office, setOffice] = useState(false);
  const [origins, setOrigins] = useState([]);
  const [origin, setOrigin] = useState('');
  const [originLoading, setOriginLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [originError, setOriginError] = useState('');
  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    setLoading(true); setContacts([]); setSelected(''); setPreferred(null);
    setOffice(false); setOrigins([]); setOrigin(''); setError(''); setOriginError('');
    axios.get(`${API_BASE_URL}/api/support/options`, { withCredentials: true }).then(({ data }) => {
      if (!active) return;
      const rows = supportContacts({ support_contacts: data?.contacts });
      const pref = rows.some(c => c.id === data?.preferred_id) ? data.preferred_id : null;
      setContacts(rows); setPreferred(pref);
      setSelected(pref || (rows.length === 1 ? rows[0].id : ''));
    }).catch(() => { if (active) setError('Não foi possível carregar os números de suporte. Feche e tente novamente.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [open]);
  useEffect(() => {
    if (!open || !office || !hasAtendimento) return undefined;
    let active = true;
    setOriginLoading(true); setOriginError('');
    axios.get(`${API_BASE_URL}/api/support/connections`, { withCredentials: true }).then(({ data }) => {
      if (!active) return;
      const rows = Array.isArray(data) ? data : [];
      setOrigins(rows);
      const usable = rows.filter(c => c.status === 'connected' && c.phone);
      setOrigin(usable.length === 1 ? usable[0].id : '');
    }).catch(() => { if (active) setOriginError('Não foi possível carregar seus WhatsApps. Você pode usar o WhatsApp externo.'); })
      .finally(() => { if (active) setOriginLoading(false); });
    return () => { active = false; };
  }, [open, office, hasAtendimento]);
  const target = contacts.find(c => c.id === selected);
  const sender = origins.find(c => c.id === origin);
  const usable = sender?.status === 'connected' && sender?.phone && sender.phone !== target?.phone;
  async function openOffice() {
    if (busy || !target || !usable) return;
    setBusy(true); setOriginError('');
    try {
      const { data } = await axios.post(`${API_BASE_URL}/api/support/open-conversation`, {
        support_id: target.id, expected_phone: target.phone, connection_id: origin,
      }, { withCredentials: true });
      if (!data?.conversation_id || !data?.conversation?.id) throw new Error('missing conversation');
      onAtendimento?.(data.conversation);
      onOpenChange?.(false);
    } catch (e) {
      const detail = e.response?.data?.detail;
      setOriginError(typeof detail === 'string' ? detail : 'Não foi possível abrir a conversa. Tente novamente.');
    } finally { setBusy(false); }
  }
  return <Dialog open={open} onOpenChange={value => { if (!busy) onOpenChange?.(value); }}>
    <DialogContent className="cs-support-dialog sm:max-w-lg" data-testid="support-choice-modal">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2"><Headphones className="h-5 w-5 text-emerald-500" />{supportLabel}</DialogTitle>
        <DialogDescription>Escolha o número e como prefere conversar com nossa equipe.</DialogDescription>
      </DialogHeader>
      <div className="cs-support-note"><ShieldCheck size={19} /><span>Os números abaixo oferecem o mesmo suporte. Seu cadastro permanece nos registros do Office para a equipe consultar. Cada número mantém seu próprio histórico de conversa.</span></div>
      {loading && <p role="status">Consultando seu atendimento…</p>}
      {error && <p role="alert">{error}</p>}
      {!loading && !error && !contacts.length && <p>Nenhum número de suporte está disponível. Peça ao administrador para configurar.</p>}
      {!!contacts.length && <fieldset className="cs-support-destinations" disabled={busy}>
        <legend>Número de suporte</legend>
        <div className="cs-number-list">{contacts.map(c => <label key={c.id} className="cs-number-card" data-selected={selected === c.id}>
          <input type="radio" name="support-destination" checked={selected === c.id} onChange={() => setSelected(c.id)} />
          <Smartphone size={19} /><span><strong>{c.label}</strong><small>+{c.phone}</small>
            {c.id === preferred && <small className="cs-history-badge"><History size={13} />Seu atendimento mais recente</small>}</span>
        </label>)}</div>
        {!preferred && <p className="cs-support-hint">Sem histórico identificado para indicar uma preferência. Escolha um dos números.</p>}
      </fieldset>}
      {!!contacts.length && <div className="grid gap-2">
        <button type="button" className="cs-support-action" disabled={!target || !hasAtendimento || busy}
          aria-expanded={office} onClick={() => setOffice(v => !v)} data-testid="support-choice-atendimento">
          <MessagesSquare size={21} /><span><strong>Atendimento do Office</strong><small>{hasAtendimento ? 'Use o WhatsApp da sua revenda e acompanhe a conversa por aqui.' : 'Seu acesso não inclui Atendimentos. Use o WhatsApp externo.'}</small></span>
        </button>
        {office && <div className="cs-support-origin">
          <label htmlFor="support-origin">Seu WhatsApp para conversar</label>
          <select id="support-origin" value={origin} onChange={e => setOrigin(e.target.value)} disabled={busy || originLoading}>
            <option value="">{originLoading ? 'Carregando suas conexões…' : 'Selecione seu WhatsApp'}</option>
            {origins.map(c => <option key={c.id} value={c.id} disabled={c.status !== 'connected' || !c.phone || c.phone === target?.phone}>
              {c.name} {c.phone ? `· +${c.phone}` : ''}{c.status !== 'connected' ? ' · Desconectado' : ''}
            </option>)}
          </select>
          {!originLoading && !originError && !origins.some(c => c.status === 'connected' && c.phone) && <p>Conecte um WhatsApp da sua revenda ou use a opção externa abaixo.</p>}
          {originError && <p role="alert">{originError}</p>}
          <button type="button" onClick={openOffice} disabled={!usable || busy || !target} data-testid="support-open-office">{busy ? 'Abrindo…' : 'Abrir conversa no Office'}</button>
          <small>Nenhuma mensagem será enviada automaticamente.</small>
        </div>}
        {target ? <a href={supportHref(target)} target="_blank" rel="noopener noreferrer" className="cs-support-action" data-testid="support-choice-whatsapp">
          <WhatsAppIcon size={21} /><span><strong>WhatsApp externo</strong><small>Abra o WhatsApp para conversar com +{target.phone}.</small></span>
        </a> : <p className="cs-support-hint">Selecione um número para liberar as opções de atendimento.</p>}
      </div>}
    </DialogContent>
  </Dialog>;
}
